import { Message } from 'element-ui'

// 错误类型
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  AUTH: 'AUTH',
  VALIDATION: 'VALIDATION',
  BUSINESS: 'BUSINESS',
  UNKNOWN: 'UNKNOWN'
}

// 错误处理类
export class ErrorHandler {
  constructor() {
    this.errorMap = new Map()
    this.setupGlobalErrorHandling()
  }

  // 设置全局错误处理
  setupGlobalErrorHandling() {
    // 捕获未处理的Promise错误
    window.addEventListener('unhandledrejection', (event) => {
      // 忽略用户取消操作（Element UI的 $confirm、$prompt 等）
      if (event.reason === 'cancel' || event.reason === 'close') {
        console.log('用户取消操作，已忽略')
        event.preventDefault()
        return
      }
      
      // 忽略 Vue Router 导航取消和导航重复错误
      const reason = event.reason
      if (reason) {
        // 检查是否是导航取消错误
        if (reason.name === 'NavigationCancelled' || 
            reason.name === 'NavigationDuplicated' ||
            (typeof reason.message === 'string' && (
              reason.message.includes('Navigation cancelled') ||
              reason.message.includes('Navigation cancelled from') ||
              reason.message.includes('Avoided redundant navigation')
            ))) {
          console.log('路由导航取消或重复，已忽略')
          event.preventDefault()
          return
        }
      }
      
      console.error('Unhandled promise rejection:', event.reason)
      this.handleError(event.reason)
      event.preventDefault()
    })

    // 捕获JavaScript错误
    window.addEventListener('error', (event) => {
      console.error('JavaScript error:', event.error)
      this.handleError(event.error)
    })
  }

  // 处理错误
  handleError(error, context = {}) {
    // 忽略 Vue Router 导航取消错误（如 Navigation cancelled from ... with a new navigation）
    if (error && typeof error.message === 'string' && error.message.indexOf('Navigation cancelled from') !== -1) {
      console.log('路由导航取消，已忽略')
      return
    }

    // 忽略Element UI clickoutside指令的DOM访问错误
    if (error && error.message && (
      error.message.includes('Cannot read properties of null (reading \'setAttribute\')') ||
      error.message.includes('Cannot read properties of undefined (reading \'_wrapper\')')
    ) && error.stack && (
      error.stack.includes('element-ui.common.js') ||
      error.stack.includes('clickoutside.js') ||
      error.stack.includes('removeTabindex')
    )) {
      console.log('Element UI DOM清理错误，已忽略:', error.message)
      return
    }

    const errorInfo = this.parseError(error)
    
    // 记录错误
    this.logError(errorInfo, context)
    
    // 显示用户友好的错误消息
    this.showErrorMessage(errorInfo)
    
    // 上报错误（可选）
    this.reportError(errorInfo, context)
  }

  // 解析错误
  parseError(error) {
    let errorInfo = {
      type: ERROR_TYPES.UNKNOWN,
      message: '未知错误',
      code: null,
      stack: null
    }

    if (error) {
      // 网络错误
      if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        errorInfo.type = ERROR_TYPES.NETWORK
        errorInfo.message = '网络连接失败，请检查网络设置'
      }
      // 认证错误
      else if (error.response?.status === 401 || error.code === 'UNAUTHORIZED') {
        errorInfo.type = ERROR_TYPES.AUTH
        errorInfo.message = '' // 不显示提示信息，静默处理
      }
      // 验证错误
      else if (error.response?.status === 400 || error.code === 'VALIDATION_ERROR') {
        errorInfo.type = ERROR_TYPES.VALIDATION
        errorInfo.message = error.response?.data?.message || error.message || '请求参数错误'
      }
      // 业务错误
      else if (error.response?.data?.message) {
        errorInfo.type = ERROR_TYPES.BUSINESS
        errorInfo.message = error.response.data.message
      }
      // 其他错误
      else {
        errorInfo.message = error.message || '系统错误，请稍后重试'
      }

      errorInfo.code = error.code || error.response?.status
      errorInfo.stack = error.stack
    }

    return errorInfo
  }

  // 记录错误
  logError(errorInfo, context) {
    const logData = {
      timestamp: new Date().toISOString(),
      error: errorInfo,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    // 开发环境打印详细错误
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Details')
      console.error('Error Info:', errorInfo)
      console.error('Context:', context)
      console.error('Stack:', errorInfo.stack)
      console.groupEnd()
    }

    // 生产环境可以发送到日志服务
    if (process.env.NODE_ENV === 'production') {
      // 这里可以集成第三方日志服务
      console.error('Error logged:', logData)
    }
  }

  // 显示错误消息
  showErrorMessage(errorInfo) {
    // 避免重复显示相同错误
    const errorKey = `${errorInfo.type}-${errorInfo.message}`
    if (this.errorMap.has(errorKey)) {
      return
    }

    this.errorMap.set(errorKey, Date.now())
    
    // 清理过期的错误记录
    setTimeout(() => {
      this.errorMap.delete(errorKey)
    }, 5000)

    // 根据错误类型显示不同的消息
    switch (errorInfo.type) {
      case ERROR_TYPES.NETWORK:
        Message.error({
          message: errorInfo.message,
          duration: 5000,
          showClose: true
        })
        break
      case ERROR_TYPES.AUTH:
        Message.warning({
          message: errorInfo.message,
          duration: 5000,
          showClose: true
        })
        // 可以在这里触发登出操作
        break
      case ERROR_TYPES.VALIDATION:
        Message.warning({
          message: errorInfo.message,
          duration: 3000
        })
        break
      case ERROR_TYPES.BUSINESS:
        Message.error({
          message: errorInfo.message,
          duration: 4000
        })
        break
      default:
        Message.error({
          message: errorInfo.message,
          duration: 4000
        })
    }
  }

  // 上报错误
  reportError(errorInfo, context) {
    // 这里可以集成错误监控服务，如Sentry
    if (process.env.NODE_ENV === 'production') {
      // 示例：发送到错误监控服务
      // Sentry.captureException(error, { extra: context })
    }
  }

  // 手动报告错误
  report(error, context = {}) {
    this.handleError(error, context)
  }
}

// 创建全局错误处理实例
export const errorHandler = new ErrorHandler()

// Vue插件
export default {
  install(Vue) {
    Vue.prototype.$errorHandler = errorHandler
    
    // 全局错误处理
    Vue.config.errorHandler = (error, vm, info) => {
      errorHandler.handleError(error, {
        component: vm?.$options.name,
        info
      })
    }
  }
} 